/** @jsxImportSource theme-ui */

import React, { PropsWithChildren, useCallback } from 'react';
import { ThemeUIStyleObject, SxProp } from 'theme-ui';

import { isKeyboardSelect } from 'utils/accessibilityHelpers';
import { focusStyles, sxtheme } from 'utils/cssHelpers';

import { IClickable, IWithClassName } from 'components/_common/common-props';
import { Typography } from 'components/_common/Typography';
import { VariantLiteral } from 'components/_common/Typography/Typography';

type BdPrimaryLinkProps = IClickable &
  SxProp &
  IWithClassName & {
    /** The Typeography of the link */
    variant?: VariantLiteral;
    /** A location to navigate to if used for navigation */
    href?: string;
    /** If the link is inline with it's current text or not */
    inline?: boolean;
    /**
     * Disables tabbing to the link.
     *
     * Used when BdPrimaryLink is a trigger for tooltips because BdTooltip already wraps the trigger in interactive span, which is tabbable
     */
    noTab?: boolean;
    /** A react element or text for the link to appear as and triggers the onClick or href functionality */
    children?: React.ReactNode | string;
    invert?: boolean;
  };
const BdPrimaryLink: React.FC<PropsWithChildren<BdPrimaryLinkProps>> = ({
  onClick,
  href,
  variant = 'body2',
  children,
  inline = false,
  noTab = false,
  invert = false,
  ...props
}) => {
  const onKeyPress = useCallback(
    (e: React.KeyboardEvent<HTMLAnchorElement>) => {
      if (isKeyboardSelect(e) && onClick) {
        e.preventDefault();
        onClick(e as any);
      }
    },
    [onClick]
  );

  return (
    <a
      data-testid="primary-link"
      href={href?.length ? href : null}
      tabIndex={noTab ? undefined : 0}
      onClick={onClick}
      onKeyPress={onKeyPress}
      role="button"
      sx={{
        display: inline ? 'inline-block' : 'block',
        width: inline ? null : 'fit-content',
        color: invert ? 'white' : sxtheme(t => t.colors.sea80),
        cursor: 'pointer',
        outline: 'none',
        '&:hover': {
          color: invert ? 'white' : sxtheme(t => t.colors.sea90),
          textDecoration: 'underline',
        },
        '&:focus': focusStyles({ invert }),
        '&:active': {
          color: invert ? 'white' : sxtheme(t => t.colors.sea100),
          textDecoration: 'underline',
        },
        ...props.sx,
      }}
      {...props}
    >
      <Typography
        data-testid="primary-link-text"
        className="primary-link-text"
        variant={variant}
        marginBottom="0"
        inline={inline}
      >
        {children}
      </Typography>
    </a>
  );
};

export default BdPrimaryLink;
